import React from "react";
import "./style.scss";
import TextTitle from "../common/ui/typography/TextTitle";
import Button from "../common/ui/buttons/Button";
import { Tabs } from "../common/ui/tabs/Tabs";
import { Tab } from "../common/ui/tabs/Tab";
import { useReferralStore } from "./store";
import Table from "../common/Table/Table";
import { TableColumn } from "../common/Table/TableTypes";
import { useGlobalStore } from "../../store";
import { useInfiniteQuery, useMutation } from "react-query";
import { referrals } from "../../api/referral";
import Menu from "../common/ui/menu/Menu";
import moment from "moment";
import Icon from "../common/ui/Icon";
import TextBody from "../common/ui/typography/TextBody";
import { useAlert } from "../../hooks/useAlert";
import { useConfirm } from "../../hooks/useConfirm";

export const Referral = () => {
  const { activeTabIndex, setActiveTab, reset } = useReferralStore();
  const selectedCompany = useGlobalStore((state) => state.selectedCompany);
  const { showAlert } = useAlert();
  const { confirm } = useConfirm();

  const status = activeTabIndex === 0 ? "active" : "expired";

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery(
    ["referrals-list-by-eld", selectedCompany, status],
    ({ pageParam = 1 }) =>
      referrals.listByEld(selectedCompany, pageParam, 10, status),
    {
      getNextPageParam: (_lastPage, allPages) => {
        const total = _lastPage?.data?.count ?? 0;
        const perPage = 10;
        const loadedItems = allPages.length * perPage;

        return loadedItems < total ? allPages.length + 1 : undefined;
      },

      enabled: !!selectedCompany,
    }
  );

  const getCompanyUrls = (company: string) => {
    switch (company) {
      case "tteld":
        return {
          visible: "https://app.tteld.com/new/",
          signUp: "https://app.tteld.com/new/#/sign-up",
        };

      case "uateld":
        return {
          visible: "https://uateld.test.tteld.com/",
          signUp: "https://uateld.test.tteld.com/#/sign-up",
        };

      case "evoeld":
        return {
          visible: "https://dash.evoeld.com/new/",
          signUp: "https://dash.evoeld.com/new/#/sign-up",
        };

      case "ontime-logs":
        return {
          visible: "https://dash.ontime-logs.com/new/",
          signUp: "https://dash.ontime-logs.com/new/#/sign-up",
        };

      case "zippyeld":
        return {
          visible: "https://dash.zippyeld.com/new/",
          signUp: "https://dash.zippyeld.com/new/#/sign-up",
        };

      case "rulereld":
        return {
          visible: "https://dash.rulereld.com/new/",
          signUp: "https://dash.rulereld.com/new/#/sign-up",
        };

      case "fiveeld":
        return {
          visible: "https://dash.fiveeld.com/new/",
          signUp: "https://dash.fiveeld.com/new/#/sign-up",
        };

      case "fleetvision":
        return {
          visible: "https://dash.fleetvision.co/new/",
          signUp: "https://dash.fleetvision.co/new/#/sign-up",
        };

      case "logfleet":
        return {
          visible: "https://dash.logfleet.net/new/",
          signUp: "https://dash.logfleet.net/new/#/sign-up",
        };

      case "zeelog":
        return {
          visible: "https://dash.zeelog.com/new/",
          signUp: "https://dash.zeelog.com/new/#/sign-up",
        };

      default:
        return {
          visible: "https://app.tteld.com/new/",
          signUp: "https://app.tteld.com/new/#/sign-up",
        };
    }
  };

  const columns: TableColumn<any>[] = [
    {
      header: "#",
      accessor: "num",
      maxWidth: "60px",
    },
    {
      header: "Service Name",
      accessor: "name",
      maxWidth: "260px",
      cellClass: "text-ellipsis",
    },
    {
      header: "Service Manager",
      accessor: "service_manager",
      maxWidth: "180px",
    },
    {
      header: "Added by",
      accessor: "added_by",
      maxWidth: "180px",
    },
    {
      header: "Referral Link",
      accessor: "link",
      maxWidth: "260px",
      cellClass: "text-ellipsis",
    },
    {
      header: "Referral Code",
      accessor: "code",
      maxWidth: "110px",
    },
    {
      header: "Registered Companies",
      accessor: "companyCount",
      maxWidth: "130px",
    },
    {
      header: "Created at",
      accessor: "created_at",
      maxWidth: "180px",
    },
    {
      header: "Actions",
      accessor: "actions",
      align: "end",
      cellClass: "text-end",
    },
  ];

  const allItems =
    data?.pages?.flatMap((page) =>
      page.data.map((item: any, index: number) => {
        const urls = getCompanyUrls(selectedCompany);

        return {
          num: index + 1,
          id: item.id,
          name: item.name,
          service_name: item.name,
          service_manager: item.manager,
          added_by: `${item.addedBy?.first_name || ""} ${item.addedBy?.second_name || ""}`,
          code: item.code,
          companyCount: item.companyCount,
          created_at: moment(item.createdAt).format("MMM D, YYYY hh:mm A"),

          link: `${urls.signUp}?ref=${item.id}`,

          visibleLink: urls.visible,

          actions: "",
        };
      })
    ) ?? [];

  const editByEldMutation = useMutation({
    mutationFn: ({
      referralId,
      eldType,
      status,
    }: {
      referralId: string | number;
      eldType: string;
      status: string;
    }) => referrals.editByEld(referralId, eldType, { status }),

    onSuccess: (data) => {
      showAlert(
        "success",
        data?.data?.data?.message || "Successfully updated!"
      );
    },

    onError: (error: any) => {
      showAlert(
        "error",
        error?.response?.data?.error.message || "Something went wrong!"
      );
    },
  });

  const handleArchive = async (referralId: string | number, status: string) => {
    const isExpiring = status === "expired";

    const title = isExpiring
      ? "Are you sure you want to expire this link?"
      : "Are you sure you want to activate this link?";

    const subtitle = isExpiring
      ? "Expiring the link will deactivate it and move it to the Expired tab. You can reactivate or delete it from there."
      : "Activating this link will restore it and make it available again.";

    const ok = isExpiring ? "Expire" : "Activate";

    const type = isExpiring ? "danger" : "success";

    const confirmed = await confirm({
      title,
      subtitle,
      ok,
      type,
    });

    if (confirmed) {
      editByEldMutation.mutate({
        referralId,
        eldType: selectedCompany,
        status,
      });
    }
  };

  return (
    <div>
      <div className={"referral__header d-flex align-items-center"}>
        <TextTitle level="large" className="d-flex align-center gap-2">
          Referral List
        </TextTitle>
        <div style={{ marginLeft: "auto" }} className={"d-flex gap-2"}>
          <Button
            main
            to={"/referral/create"}
            beforeIcon={"plus"}
            beforeClass="referral__button"
          >
            <span className="referral__button--text">Add New Link</span>
          </Button>
          <Button
            beforeIcon={"arrow-clockwise"}
            onClick={() => refetch()}
            beforeClass="referral__button"
          >
            <span className="referral__button--text">Refresh</span>
          </Button>
        </div>
      </div>
      <Tabs modelValue={activeTabIndex} onTabChange={setActiveTab}>
        <Tab
          icon="text-bullet-list-square"
          activeIcon="text-bullet-list-square-filled"
          text="Active Links"
        />
        <Tab icon="archive" activeIcon="archive-filled" text="Expired Links" />
      </Tabs>
      <div className="referral-table-wrapper">
        <Table
          columns={columns}
          data={allItems}
          isLoading={isLoading}
          renderRow={(item, column, rowIndex) => {
            if (column.accessor === "num") {
              return <>{rowIndex + 1}</>;
            }
            if (column.accessor === "actions") {
              return (
                <>
                  {activeTabIndex ? (
                    <Menu
                      activator={({ onClick }) => (
                        <Button
                          beforeIcon={"more_filled"}
                          beforeClass="mr-0"
                          size={"xs"}
                          onClick={onClick}
                        />
                      )}
                      isOpen={false}
                      onClose={() => {}}
                    >
                      <div
                        className="menu__item"
                        onClick={() => {
                          handleArchive(item.id, "active");
                        }}
                      >
                        <Icon icon={"archive-blue"} />
                        <TextBody
                          level={"1"}
                          className="d-flex align-center gap-2"
                        >
                          Active
                        </TextBody>
                      </div>
                    </Menu>
                  ) : (
                    <Menu
                      activator={({ onClick }) => (
                        <Button
                          beforeIcon={"more_filled"}
                          beforeClass="mr-0"
                          size={"xs"}
                          onClick={onClick}
                        />
                      )}
                      isOpen={false}
                      onClose={() => {}}
                    >
                      {/* <div
                        className="menu__item"
                        onClick={() => {
                          // navigate(`/referral/edit/${item.id}`);
                        }}
                      >
                        <Icon icon={"edit"} />
                        <TextBody
                          level={"1"}
                          className="d-flex align-center gap-2"
                        >
                          Edit
                        </TextBody>
                      </div> */}
                      <div
                        className="menu__item"
                        onClick={() => {
                          handleArchive(item.id, "expired");
                        }}
                      >
                        <Icon icon={"archive-red"} />
                        <TextBody
                          level={"1"}
                          className="d-flex align-center gap-2"
                        >
                          Expired
                        </TextBody>
                      </div>
                    </Menu>
                  )}
                </>
              );
            }
            if (column.accessor === "link") {
              return (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ellipsis"
                >
                  {item.visibleLink}
                </a>
              );
            }
            return item[column.accessor];
          }}
        />
      </div>
    </div>
  );
};
